import { useCallback, useEffect, useRef, useState } from 'react';
import { Dimensions, Platform, type View } from 'react-native';
import { UnistylesRuntime } from 'react-native-unistyles';

import { subscribeScrollVisibility } from '@/core/scroll-visibility';

function isCompactLayout() {
  return ['xs', 'sm'].includes(UnistylesRuntime.breakpoint);
}

type UseAnimateWhenInViewOptions = {
  threshold?: number;
  rootMargin?: string;
};

export function useAnimateWhenInView(options?: UseAnimateWhenInViewOptions) {
  const isCompact = isCompactLayout();
  const [isInView, setIsInView] = useState(() => !isCompact);
  const ref = useRef<View>(null);
  const hasTriggeredRef = useRef(isInView);

  useEffect(() => {
    if (!isCompact) {
      hasTriggeredRef.current = true;
      setIsInView(true);
      return;
    }

    hasTriggeredRef.current = false;
    setIsInView(false);
  }, [isCompact]);

  const markVisible = useCallback(() => {
    if (hasTriggeredRef.current) {
      return;
    }

    hasTriggeredRef.current = true;
    setIsInView(true);
  }, []);

  const checkVisibility = useCallback(() => {
    if (!isCompact || hasTriggeredRef.current) {
      return;
    }

    ref.current?.measureInWindow((_x, y, _width, height) => {
      if (height <= 0) {
        return;
      }

      const windowHeight = Dimensions.get('window').height;
      if (y < windowHeight && y + height > 0) {
        markVisible();
      }
    });
  }, [isCompact, markVisible]);

  useEffect(() => {
    if (!isCompact) {
      return;
    }

    checkVisibility();

    let observer: IntersectionObserver | undefined;
    let observerFrame: number | undefined;

    if (Platform.OS === 'web' && typeof IntersectionObserver !== 'undefined') {
      observerFrame = requestAnimationFrame(() => {
        const node = ref.current as unknown as Element | null;
        if (!node) {
          checkVisibility();
          return;
        }

        observer = new IntersectionObserver(
          (entries) => {
            if (entries[0]?.isIntersecting) {
              markVisible();
              observer?.disconnect();
            }
          },
          {
            threshold: options?.threshold ?? 0.12,
            rootMargin: options?.rootMargin ?? '0px 0px -5% 0px',
          }
        );
        observer.observe(node);
      });
    }

    const unsubscribeScroll = subscribeScrollVisibility(checkVisibility);

    return () => {
      if (observerFrame !== undefined) {
        cancelAnimationFrame(observerFrame);
      }
      observer?.disconnect();
      unsubscribeScroll();
    };
  }, [
    checkVisibility,
    isCompact,
    markVisible,
    options?.rootMargin,
    options?.threshold,
  ]);

  const onLayout = useCallback(() => {
    checkVisibility();
    requestAnimationFrame(() => {
      checkVisibility();
    });
  }, [checkVisibility]);

  return { ref, isInView, onLayout };
}
